import { makeAutoObservable } from "mobx";
import dayjs, { Dayjs } from 'dayjs';
import ApiStore from "./ApiStore";
import { ProjectDocument } from "./ProjectDocumentationStore";

export interface AnonymousAccess {
  id: string;
  jwtToken: string;
  endDate: Dayjs;
  createdAt: Dayjs;
  projectId: string;
  companyId: string;
  authorId: string;
}

/**
 * Public-link state that is not the board: the link list and the documentation read through the
 * board token. The public kanban board itself is `KanbanTaskStore` in `setMode('public')` (Task 9).
 */
class AnonymousStore {
  ApiStore!: ApiStore;
  accesses: AnonymousAccess[] = [];
  documents: ProjectDocument[] = [];
  isLoading: boolean = false;
  isExporting: boolean = false;

  constructor(apiStore: ApiStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
  }

  getDocumentationByProject = async (): Promise<ProjectDocument[]> => {
    this.isLoading = true;
    const tasks = await this.ApiStore.AnonymousApiClient.getDocumentationByProject().finally(() => {
      this.isLoading = false;
    });
    if (tasks && tasks.length) {
      this.documents = tasks.map((el: { deadLine: string; startDate: string; }) => ({
        ...el,
        startDate: dayjs(el.startDate),
        dueDate: dayjs(el.deadLine),
      }));
    }
    return tasks;
  }

  exportDocumentation = async (projectId: string, typeDocument: 'pdf' | 'docx'): Promise<void> => {
    this.isExporting = true;
    try {
      const response = await this.ApiStore.AnonymousApiClient.ExportDocumentation(projectId, typeDocument);
      // Axios hands the Blob back in `data` (the client asks for responseType 'blob').
      const blob: Blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = typeDocument === 'pdf' ? 'Documentation.pdf' : 'Documentation.docx';
      // The anchor must be in the DOM before click() for the download to start in every browser.
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading file:', error?.message || error);
      throw error;
    } finally {
      this.isExporting = false;
    }
  }

  patchDocumentation = async (docId: string, patchData: any): Promise<any> => {
    this.isLoading = true;
    try {
      return await this.ApiStore.AnonymousApiClient.patchDocumentation(docId, patchData);
    } finally {
      this.isLoading = false;
    }
  }
}

export default AnonymousStore;
